import { PERFORMANCE_CALCULATOR_ID } from 'constants/calculators'
import type { HeatEnergyType } from 'projectSections/sections/energy/Heat/types'
import type { NaturalGasEnergyType } from 'projectSections/sections/energy/NaturalGas/utils'
import type { VehicleEnergyType } from 'projectSections/sections/energy/Transportation/utils'
import { EnergyTypeEnum } from 'projectSections/sections/energy/types'
import { StdFieldErrorType } from 'reducer/project/projectErrorsReducer'
import {
  SNOW_MODE_DYNAMIC,
  SNOW_MODE_FIXED_PERCENTAGE,
  SNOW_PROVIDER_CHOICES,
} from 'resources/projectConfigurations/SnowLosses/constants'
import { AvailableCustomerActionsType } from './customerActions'
import { EventCategoryEnum, EventFormVariant, EventIconVariant } from './events'
import { ImageryActivation } from './imagery'
import { PrivateFileType } from './privateFile'
import { ProjectTransactionType, ProjectTransactionTypeV3 } from './projectTransactions'
import { ProposalDataType, SelectedProjectProposalDataType } from './proposals'
import { PermissionSets, RoleType } from './roles'
import { StudioSystemType } from './studio/items'
import { SystemDataType } from './systems'
import { TestimonialType } from './testimonials'
import { NormalizedUsageType } from './usage'
import { UtilityType } from './utilities'
import { GroupType } from 'app/src/elements/input/GroupedSelectInput'
import { Icon3Props } from 'opensolar-ui'
import type { ProjectServiceCategory } from 'util/projectServiceCategories'
import { PROJECT_SERVICE_CATEGORY_DEFINITIONS } from 'util/projectServiceCategories'

type Statement = {
  balance: number
  label: string
  value: number
}

export type CerMappingEntry = {
  os_code: string
  cer_code: string
  os_manufacturer: string
  cer_manufacturer: string
  module_quantity?: number
}

//TODO: add 'new' to this type, will cause a lot of (important) downstream complaints
export type ProjectId = number // | 'new'

export type BaseOtherEnergyItemType = {
  energy_type?: EnergyTypeEnum
  is_current?: boolean
  is_active?: boolean
  other_component_type?: string
}

export const QuoteModeEnum = {
  NORMAL: 0,
  QUICK_QUOTE: 1,
} as const

export type QuoteMode = (typeof QuoteModeEnum)[keyof typeof QuoteModeEnum]

export const QuoteModeDefinitions: Record<QuoteMode, { value: QuoteMode; name: string }> = {
  [QuoteModeEnum.NORMAL]: { value: QuoteModeEnum.NORMAL, name: 'Normal' },
  [QuoteModeEnum.QUICK_QUOTE]: { value: QuoteModeEnum.QUICK_QUOTE, name: 'Quick Quote' },
}

export const QUICK_QUOTE_BADGE_LABEL = 'Quick Quote'

export const QUOTE_MODE_FILTER_CHOICES = Object.values(QuoteModeDefinitions).map(({ value, name }) => ({
  id: value,
  name,
}))

/** Home-page quote type dropdown copy (list/filter labels stay on {@link QuoteModeDefinitions}). */
export type HomeQuoteModeOption = {
  value: QuoteMode
  labelKey: string
  descriptionKey: string
  tooltipKey: string
  icon: Icon3Props['icon']
  showNewBadge?: boolean
}

export const HomeQuoteModeOptions: Record<QuoteMode, HomeQuoteModeOption> = {
  [QuoteModeEnum.NORMAL]: {
    value: QuoteModeEnum.NORMAL,
    labelKey: 'Design Project',
    descriptionKey: 'Create a system design and generate your proposal with our standard flow.',
    tooltipKey: 'Best for solar, battery storage, EV charging, and projects that require system design.',
    icon: 'design_services',
  },
  [QuoteModeEnum.QUICK_QUOTE]: {
    value: QuoteModeEnum.QUICK_QUOTE,
    labelKey: 'Quick Quote',
    descriptionKey: 'Generate a quote for your organisations services with our simplified flow.',
    tooltipKey: "Best for HVAC, heat pumps, roofing, and other services that don't require system design.",
    icon: 'acute',
    showNewBadge: true,
  },
}

export type { ProjectServiceCategory } from 'util/projectServiceCategories'

export type ProjectServiceTypeData = {
  service_type: ProjectServiceCategory
  manually_selected: boolean
}

export const PROJECT_SERVICE_CATEGORY_CHOICES: { id: ProjectServiceCategory; name: string }[] =
  PROJECT_SERVICE_CATEGORY_DEFINITIONS.map(({ id, name }) => ({ id, name }))

export type OtherEnergyItemType = HeatEnergyType | VehicleEnergyType | NaturalGasEnergyType

/*
Note: is_commercial is not available on the project model. It can be used as a derived field but
it should never be used where we are treating it as a possibly dynamic field on the project form.
*/
export interface BaseProjectType {
  id: ProjectId
  url?: URL
  is_residential?: boolean
  title?: string
  identifier: string
  quote_mode?: QuoteMode
  service_types?: string | ProjectServiceCategory[]
  service_types_data?: ProjectServiceTypeData[]
  business_name: string | null
  address: string | null
  zip: string | null
  state: string | null
  language: string | null
  language_override: string | null
  locality: string | null
  country: URL
  county: string | null
  stage: number
  country_iso2: string | null
  lat: number
  lon: number
  contract_date: Date | null
  //Todo: add contact model
  contacts?: any[]
  contacts_new?: ContactData[]
  contacts_data?: ContactData[]
  design?: string
  org_id: number
  assigned_role?: RoleType
  priority?: number
  created_date: Date | null
  modified_date: Date | null
  usage?: string
  //Todo: fix this type
  utility_tariff_current: any | null
  utility_tariff_proposed: any | null
  utility_tariff_current_custom: any | null
  utility_tariff_proposed_custom: any | null
  assigned_team_member: RoleType
  utility_tariff_or_guess: UtilityType
  utility_tariff_proposed_or_guess: UtilityType
  usage_annual_or_guess: number
  available_customer_actions?: AvailableCustomerActionsType
  actions?: StageActionType
  statement: Statement[] | null
  original_system_sold?: string | null
  system_sold?: string | null
  systems?: SystemDataType[]
  payment_option_sold?: URL | null
  simulate_first_year_only?: boolean
  api_key_chat?: string
  usage_normalized?: NormalizedUsageType
  testimonials?: TestimonialType[]
  customer_proposal_data?: ProposalDataType
  stage_name?: string
  lead_source?: string
  tag_names?: string
  valid_until_date?: string | null
  installation_date?: string | null
  business_identifier?: string
  meter_identifier?: string
  utility_tariff_current_code?: string
  utility_tariff_proposed_code?: string
  proposal_message?: string
  wind_region?: any | null
  has_cellular_coverage?: number
  power_factor?: any | null
  number_of_storeys: number | null
  number_of_phases: number | null
  number_of_wires: number | null
  parcel_identifier?: string
  serial_numbers_panels?: string
  serial_numbers_inverters?: string
  serial_numbers_batteries?: string
  years_to_simulate?: number
  is_pricing_locked?: boolean
  roof_type_name?: string
  site_notes?: string
  notes?: string
  proposal_content?: string
  contract_terms?: string
  transactions_data?: ProjectTransactionType[] | ProjectTransactionTypeV3[]
  events_data?: StageActionEventType[]
  greenlancer_project_id?: string | undefined
  docusign_contract_envelope_id?: string
  pandadoc_contract_document_id?: string

  access: number
  events: unknown[]
  assigned_role_data: RoleType
  assigned_role_email: string
  assigned_role_id: number
  assigned_role_name: string
  assigned_role_phone: string
  assigned_role_accreditation: string
  assigned_installer_role_data: RoleType
  assigned_installer_role: unknown
  assigned_site_inspector_role_data: RoleType
  assigned_site_inspector_role: unknown
  change_orders: ChangeOrderType[]
  initiated_change_order: number | null
  configuration_override: Partial<ProjectConfigType> | null
  configuration: ProjectConfigType
  costing_override: unknown
  costing: Record<string, any>
  contract: unknown
  country_name: string
  last_calculation_error: unknown
  calculation_error_messages: StdFieldErrorType[] | null
  other_energy_types: OtherEnergyItemType[]
  org: string
  private_files_data: PrivateFileType[]
  private_files: unknown[]
  proposal_template: unknown
  proposal_template_settings: {
    title: string
    type: unknown
    pvprosell_template_id: unknown
  }
  roof_type: string
  sold_date: Timestamp
  stars: unknown[]
  system_installed: unknown
  tags_data: TagData[]
  tags: unknown[]
  temperature_min_max: [number, number]
  temperature_max_override: number
  temperature_min_override: number
  testimonials_data: unknown[]
  timezone_offset: number
  utility_tariff_current_guess: UtilityType
  federal_income_tax_rate_percentage: unknown
  state_income_tax_rate_percentage: unknown
  custom_project_info_1: string
  custom_project_info_2: string
  custom_project_info_3: string
  custom_project_info_4: string
  custom_project_info_5: string
  allow_email_notifications: true
  brighte_role_connection_status: 'connection_missing' | 'connected' | 'no_role' | undefined
  auto_apply_max_simulate_years: false
  contract_template_mode: string
  is_lite: true
  premium_imagery_activations: ImageryActivation[]
  // activated_premium_imagery: ImageryActivation[] // TODO: remove this field, doesn't seem to actually exist
  activated_premium_imagery_wallet_product_ids: number[]
  purchased_premium_product_ids: number[]
  roof_type_id: number | null
  selected_system?: StudioSystemType
  shared_with?: ProjectShare[]
  unshared_items?: ProjectUnsharedItems
  permissions?: PermissionSets
  tax_name?: string
  workflow: ProjectWorkflowBrief
  workflows: ProjectWorkflowType
  project_sold: null | ImplicitExplicitBoolean
  project_installed: null | ImplicitExplicitBoolean
  custom_data?: Record<CustomDataKeys, Record<string, any>>
  bridgeselect_data?: {
    enabled: boolean
    jobs_data: {
      created: string
      job_id: string
      stc_count: number
      stc_status: string
      bstc_count: number
      bstc_status: string
    }[]
  }
  cer_mappings?: {
    module_codes: CerMappingEntry[]
    inverter_codes: CerMappingEntry[]
    battery_codes: CerMappingEntry[]
  }
  // Set by the backend to hide pricing in certain components. Right now,
  // it's determined by the 'Hide pricing from shared views' setting in Teams Org sharing config.
  hide_pricing?: boolean
  // Object mapping org IDs to boolean values for org-specific pricing visibility
  org_hide_pricing?: { [orgId: string]: boolean }
  component_selector_owner_id?: number
}

type CustomDataKeys = 'scaffolding' | '_solarapp_permit' | 'proposal_config_v2' | 'dlubal_us'

enum ImplicitExplicitBoolean {
  AutoEnabled = 1,
  ManuallyEnabled = 2,
}

export interface TagData {
  id: number
  title: string
  url: string
}

export interface ProjectWorkflowBrief {
  id: number
  org_id: number
  workflow_id: number
  active_stage_id: number | string
}

export interface ProjectWorkflowType extends ProjectWorkflowBrief {
  workflow_url: number
  org_name: string
  is_selected: boolean
  active_stage_title: string
}

export interface ProjectType extends BaseProjectType {
  proposal_data?: ProposalDataType
  is_commercial?: boolean
  files_data?: FileDataBrief[]
  workflow_stage_id?: number
}

export type FileDataBrief = {
  id?: number
  title: string
  system_uuid?: string
  file_contents_b64: string
  file_tag_titles: string[]
}

// the project inside of ProposalData has a different shape
export interface SelectedProjectType extends BaseProjectType {
  proposal_data?: SelectedProjectProposalDataType
  proposal_template_id?: number
}

export interface ProjectShare {
  is_shared: boolean
  org_name: string
  org_id: number
}

interface ProjectUnsharedItems {
  configuration_override?: UnsharedEntity
  proposal_template?: UnsharedEntity
  contract_by_project?: UnsharedEntity
  contract_by_system?: UnsharedEntity[]
  testimonials?: UnsharedEntity[]
  pricing_schemes?: UnsharedEntity[]
  costing_schemes?: UnsharedEntity[]
  adders?: UnsharedEntity[]
  payment_options?: UnsharedEntity[]
  battery_control_scheme?: UnsharedEntity
  components?: Object
}

interface UnsharedEntity {
  title: string
  url: string
}

export interface ProjectConfigType {
  id: number
  url: string
  org: string
  title: string
  description: string
  priority: false
  created_date: Timestamp
  modified_date: Timestamp
  performance_calculator: PERFORMANCE_CALCULATOR_ID
  weather_dataset: number
  horizon_shading_provider: number
  inverter_modelling_automation: number
  setbacks_default: number
  setbacks_gutter: number
  setbacks_ridge: number
  setbacks_valley: number
  setbacks_hip: number
  setbacks_rake: number
  setbacks_shared: number
  setbacks_flat_gutter: number
  setbacks_skylight: number
  setbacks_dormer: number
  setbacks_objects: number
  setbacks_arrays: number
  setbacks_walkway: number
  setbacks_ladder: number
  setbacks_water_tank: number
  setbacks_ac_unit: number
  setbacks_solar_water_heater: number
  setbacks_chimney: number
  setbacks_fanvent: number
  setbacks_turbine: number
  setbacks_pipe: number
  setbacks_eyebrow_vent: number
  setbacks_mast_head: number
  setbacks_satellite_dish: number
  tilt_rack_default_tilt: number
  tilt_rack_default_orientation: 'landscape' | 'portrait'
  apply_tilt_racks_below_slope: number
  years_to_simulate: number
  performance_adjustment: number
  discount_rate: number
  utility_inflation_annual: string
  feed_in_tariff_inflation_annual: 0
  utility_inflation_annual_lower_bound: number
  utility_inflation_annual_upper_bound: number
  scaling_factors_label: string
  proposed_usage_adjustment: number
  sam_string_soiling: number
  sam_string_dc_wiring: number
  sam_string_mismatch: number
  sam_micro_soiling: number
  sam_micro_dc_wiring: number
  sam_micro_mismatch: number
  sam_optimizer_soiling: number
  sam_optimizer_dc_wiring: number
  sam_optimizer_mismatch: number
  sam_ac_wiring: number
  sam_nameplate: number
  sam_diodes_and_connections: number
  sam_dc_system_availability: number
  sam_ac_system_availability: number
  sam_snow: number
  sam_snow_mode: typeof SNOW_MODE_FIXED_PERCENTAGE | typeof SNOW_MODE_DYNAMIC
  sam_snow_provider: (typeof SNOW_PROVIDER_CHOICES)[number]['id']
  show_tax_effects_of_solar: boolean
  auto_apply_max_simulate_years: boolean
  module_spacing_horizontal: number
  module_spacing_vertical: number
  end_of_life_definition: number
  end_of_life_years_override: number
  end_of_life_config?: {
    end_of_life_definition?: number
    end_of_life_years_override?: number
    battery_replacement_modelling?: number
    battery_replacement_cost_method?:
      | 'manual_cost_pre_inflation'
      | 'manual_cost_per_unit_pre_inflation'
      | 'component_cost_pre_inflation'
    battery_replacement_cost_inflation_annual?: number
    battery_replacement_cost_value?: number | null
    maximum_battery_replacement_quantity?: number | null
    inverter_end_of_life_definition?: number
    inverter_end_of_life_years_override?: number | null
    inverter_replacement_modelling?: number
    inverter_replacement_cost_method?:
      | 'manual_cost_pre_inflation'
      | 'manual_cost_per_unit_pre_inflation'
      | 'component_cost_pre_inflation'
    inverter_replacement_cost_inflation_annual?: number
    inverter_replacement_cost_value?: number | null
    maximum_inverter_replacement_quantity?: number | null
  }
}

//TODO: constrain further
export type Timestamp = string // e.g. '2022-08-31T03:39:22.225002Z'

export interface StageActionType {
  created_date: string
  events: StageActionEventType[]
  id: number
  modified_date: string
  order: number
  org: string
  share_with_orgs: string[]
  stage: number
  title: string
  url: string
}

export interface StageActionEventType {
  action_id: number
  completion_date: string
  created_date: string
  duration: number
  end: string
  event_type_id: number
  id: number
  /** Present on full Event API records; omitted from embedded `events_data` (use `who` for display). */
  user?: number | string | null
  org: string
  is_archived: boolean
  is_complete: boolean
  is_planned: boolean
  modified_date: string
  notes: string | null
  project_id: number
  project_name: string
  start: string
  task_status: number
  title: string
  custom_title?: string
  who: {
    display: string
    email: string
    portrait_image_public_url: string
  }
  categories: EventCategoryEnum[]
  form_config: EventFormVariant
  event_icon: EventIconVariant
  team_members?: string[]
}

export enum ContactDataTypeEnum {
  REAL_CONTACT = 0,
  ANONYMOUS_CONTACT = 1,
}

export interface ContactData {
  display: string
  email: string
  family_name: string
  first_name: string
  full_name: string
  id: number
  identifer: string
  org_id: number
  phone: string
  url: string
  user_id: string
  type: ContactDataTypeEnum
}

export interface ActionAssignee extends ContactData {
  portrait_image_public_url?: string
}

export enum ChangeOrderStatusEnum {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export type ChangeOrderStatus = ChangeOrderStatusEnum

export interface ChangeOrderType {
  id: number
  allow_review: boolean
  status?: ChangeOrderStatus
  published_at: string | null
  baseline_system: number | null
  contract_document: number | null
  contract_document_url: string | null
  contract_date: Date | null
  created_date: Date
  document_template: number | null
  is_complete: boolean
  payment_option_sold: number | null
  payment_option_title: string | null
  project: number | null
  system_sold: number | null
  // V3 multi-selection: system ids that the pro elected to present to the customer.
  // Payment options are derived from each system's System.payment_options M2M.
  selected_systems?: number[]
}

export interface ProjectAction extends StageActionType {
  assigned_role?: string | null
  assigned_role_detail?: ActionAssignee
  due_date?: string | null
  is_complete?: boolean
  template?: string
  workflow_stage?: string | null
  workflow_stage_id?: number | null
  team_members?: number[]
  _optimistic?: boolean
  _saving?: boolean
}
